import React from "react";
import {css} from "@panda/css";

import {CalendarCheckIcon, MapMarkerOutlineIcon, PropertyPlanIcon} from "@pg-design/icons-module";
import {Highlight, Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {useResponsiveOpenLink} from "../../../hooks/use_responsive_open_link";
import {ILatestQuery} from "../../../utils/latest_query";
import {useOfferDetails} from "../../hooks/use_offer_details";
import {IOfferBoxOffer} from "../../types/IOfferBoxOffer";
import {NoTagPlug} from "./NoTagPlug";
import {OfferBoxPill} from "./OfferBoxPill";
import {OfferBoxVendorImage} from "./OfferBoxVendorImage";
import {OfferToggleDescription} from "./OfferToggleDescription";

interface IProps {
    offer: IOfferBoxOffer;
    offerLink: string;
    isArchived?: boolean;
    unlinkVendor?: boolean;
    showFullPrice?: boolean;
    isInfoPanelOpened?: boolean;
    titleTag?: React.ElementType;
    latestQuery?: ILatestQuery & Record<string, string | string[] | null>;
    onInfoPanelCloseClick: (event: React.MouseEvent) => void;
    onOfferLinkClick?: () => void;
    onLogoClick?: () => void;
}

export const OfferBoxInfoSectionExtended = (props: IProps) => {
    const {offer, offerLink, isInfoPanelOpened, onInfoPanelCloseClick, titleTag} = props;
    const target = useResponsiveLinkTarget();
    const openResponsiveOfferLink = useResponsiveOpenLink(offerLink);

    const {addressFirstLine, addressSecondLine, constructionEndDate, priceToShow, planText} = useOfferDetails(props);

    const handleOfferLinkClick = () => {
        props.onOfferLinkClick?.();
        openResponsiveOfferLink();
    };

    return (
        <div className={offerBoxWrapperStyle}>
            {offer.description && (
                <OfferToggleDescription show={!!offer.description && isInfoPanelOpened} truncateLine={8} onClose={onInfoPanelCloseClick}>
                    {offer.description}
                </OfferToggleDescription>
            )}

            <div className={headerStyle}>
                <div className={nameColumnStyle}>
                    <a
                        href={offerLink}
                        title={offer.name}
                        target={target}
                        onMouseDown={props.onOfferLinkClick}
                        className={offerLinkStyle}
                        data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_TILE_OFFER_NAME}
                    >
                        <Text variant="headline_6" as={titleTag || "h2"} className={offerNameStyle}>
                            {offer.name}
                        </Text>
                    </a>
                    <div onClick={handleOfferLinkClick}>
                        {priceToShow ? (
                            <Text variant="body_copy_1" as="div" strong>
                                <Highlight>od {priceToShow}</Highlight>
                            </Text>
                        ) : (
                            <NoTagPlug height="2rem" />
                        )}
                    </div>
                </div>

                <OfferBoxVendorImage
                    unlinkVendor={props.unlinkVendor}
                    vendorName={offer.vendor.name}
                    vendorSlug={offer.vendor.slug}
                    vendorId={offer.vendor.id}
                    vendorLogo={offer.vendor.logo?.v_log_80x60}
                    width="72px"
                    height="54px"
                    onClick={props.onLogoClick}
                />
            </div>

            <div className={offerBoxPillWrapperStyle}>
                <OfferBoxPill icon={CalendarCheckIcon} isArchived={props.isArchived} text={constructionEndDate} />
            </div>

            <div onClick={handleOfferLinkClick} className={offerDetailListStyle}>
                <div className={infoSectionWrapperStyle}>
                    <div className={offerDetailRecordStyle}>
                        <div className={iconWrapperStyle}>
                            <MapMarkerOutlineIcon size="1.6" />
                        </div>
                        {(addressFirstLine + ", " + addressSecondLine).length > 34 ? (
                            // if the address is too long (34 letters), we divide it into two lines
                            <div className={addressContainerStyle}>
                                <Text variant="info_txt_1" className={addressWrapperStyle}>
                                    {addressFirstLine}
                                </Text>
                                <Text variant="info_txt_1" className={addressWrapperStyle}>
                                    {addressSecondLine}
                                </Text>
                            </div>
                        ) : (
                            <Text variant="info_txt_1" className={addressWrapperStyle}>
                                {addressFirstLine} {addressSecondLine}
                            </Text>
                        )}
                    </div>
                    <div className={offerDetailRecordStyle}>
                        <div className={iconWrapperStyle}>
                            <PropertyPlanIcon size="1.6" />
                        </div>
                        <Text variant="info_txt_1">{planText}</Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

const offerBoxWrapperStyle = css({
    pt: 2,
    pr: 2,
    pb: 0,
    pl: 2,
    position: "relative"
});

const headerStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    width: "100%",
    gap: 2
});

const nameColumnStyle = css({
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "auto",
    overflow: "hidden"
});

const offerLinkStyle = css({
    _hover: {
        color: "unset"
    }
});

const iconWrapperStyle = css({
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "2.4rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "2.4rem",
    height: "2.4rem"
});

const offerDetailListStyle = css({
    display: "flex",
    flexDirection: "column",
    height: "6.4rem"
});

const offerDetailRecordStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 0.5
});

const offerNameStyle = css({
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
});

const offerBoxPillWrapperStyle = css({
    mt: 2,
    mb: 2
});

const infoSectionWrapperStyle = css({
    display: "flex",
    flexDirection: "column"
});

const addressWrapperStyle = css({
    maxWidth: "27rem",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    pb: "2px"
});

const addressContainerStyle = css({
    display: "flex",
    flexDirection: "column"
});
import React from "react";
import {css} from "@panda/css";

import {CalendarCheckIcon, MapMarkerOutlineIcon, PropertyPlanIcon} from "@pg-design/icons-module";
import {Highlight, Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {useResponsiveOpenLink} from "../../../hooks/use_responsive_open_link";
import {ILatestQuery} from "../../../utils/latest_query";
import {useOfferDetails} from "../../hooks/use_offer_details";
import {IOfferBoxOffer} from "../../types/IOfferBoxOffer";
import {NoTagPlug} from "./NoTagPlug";
import {OfferBoxPill} from "./OfferBoxPill";
import {OfferBoxVendorImage} from "./OfferBoxVendorImage";
import {OfferToggleDescription} from "./OfferToggleDescription";

interface IProps {
    offer: IOfferBoxOffer;
    offerLink: string;
    isArchived?: boolean;
    unlinkVendor?: boolean;
    showFullPrice?: boolean;
    isInfoPanelOpened?: boolean;
    titleTag?: React.ElementType;
    latestQuery?: ILatestQuery & Record<string, string | string[] | null>;
    onInfoPanelCloseClick: (event: React.MouseEvent) => void;
    onOfferLinkClick?: () => void;
    onLogoClick?: () => void;
}

export const OfferBoxInfoSectionExtended = (props: IProps) => {
    const {offer, offerLink, isInfoPanelOpened, onInfoPanelCloseClick, titleTag} = props;
    const target = useResponsiveLinkTarget();
    const openResponsiveOfferLink = useResponsiveOpenLink(offerLink);

    const {addressFirstLine, addressSecondLine, constructionEndDate, priceToShow, planText} = useOfferDetails(props);

    const handleOfferLinkClick = () => {
        props.onOfferLinkClick?.();
        openResponsiveOfferLink();
    };

    return (
        <div className={offerBoxWrapperStyle}>
            {offer.description && (
                <OfferToggleDescription show={!!offer.description && isInfoPanelOpened} truncateLine={8} onClose={onInfoPanelCloseClick}>
                    {offer.description}
                </OfferToggleDescription>
            )}

            <div className={headerStyle}>
                <div className={nameColumnStyle}>
                    <a
                        href={offerLink}
                        title={offer.name}
                        target={target}
                        onMouseDown={props.onOfferLinkClick}
                        className={offerLinkStyle}
                        data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_TILE_OFFER_NAME}
                    >
                        <Text variant="headline_6" as={titleTag || "h2"} className={offerNameStyle}>
                            {offer.name}
                        </Text>
                    </a>
                    <div onClick={handleOfferLinkClick}>
                        {priceToShow ? (
                            <Text variant="body_copy_1" as="div" strong>
                                <Highlight>od {priceToShow}</Highlight>
                            </Text>
                        ) : (
                            <NoTagPlug height="2rem" />
                        )}
                    </div>
                </div>

                <OfferBoxVendorImage
                    unlinkVendor={props.unlinkVendor}
                    vendorName={offer.vendor.name}
                    vendorSlug={offer.vendor.slug}
                    vendorId={offer.vendor.id}
                    vendorLogo={offer.vendor.logo?.v_log_80x60}
                    width="72px"
                    height="54px"
                    onClick={props.onLogoClick}
                />
            </div>

            <div className={offerBoxPillWrapperStyle}>
                <OfferBoxPill icon={CalendarCheckIcon} isArchived={props.isArchived} text={constructionEndDate} />
            </div>

            <div onClick={handleOfferLinkClick} className={offerDetailListStyle}>
                <div className={infoSectionWrapperStyle}>
                    <div className={offerDetailRecordStyle}>
                        <div className={iconWrapperStyle}>
                            <MapMarkerOutlineIcon size="1.6" />
                        </div>
                        {(addressFirstLine + ", " + addressSecondLine).length > 34 ? (
                            // if the address is too long (34 letters), we divide it into two lines
                            <div className={addressContainerStyle}>
                                <Text variant="info_txt_1" className={addressWrapperStyle}>
                                    {addressFirstLine}
                                </Text>
                                <Text variant="info_txt_1" className={addressWrapperStyle}>
                                    {addressSecondLine}
                                </Text>
                            </div>
                        ) : (
                            <Text variant="info_txt_1" className={addressWrapperStyle}>
                                {addressFirstLine} {addressSecondLine}
                            </Text>
                        )}
                    </div>
                    <div className={offerDetailRecordStyle}>
                        <div className={iconWrapperStyle}>
                            <PropertyPlanIcon size="1.6" />
                        </div>
                        <Text variant="info_txt_1">{planText}</Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

const offerBoxWrapperStyle = css({
    pt: 2,
    pr: 2,
    pb: 0,
    pl: 2,
    position: "relative"
});

const headerStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    width: "100%",
    gap: 2
});

const nameColumnStyle = css({
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "auto",
    overflow: "hidden"
});

const offerLinkStyle = css({
    _hover: {
        color: "unset"
    }
});

const iconWrapperStyle = css({
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "2.4rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "2.4rem",
    height: "2.4rem"
});

const offerDetailListStyle = css({
    display: "flex",
    flexDirection: "column",
    height: "6.4rem"
});

const offerDetailRecordStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 0.5
});

const offerNameStyle = css({
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
});

const offerBoxPillWrapperStyle = css({
    mt: 2,
    mb: 2
});

const infoSectionWrapperStyle = css({
    display: "flex",
    flexDirection: "column"
});

const addressWrapperStyle = css({
    maxWidth: "27rem",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    pb: "2px"
});

const addressContainerStyle = css({
    display: "flex",
    flexDirection: "column"
});
